/**
 * TableFilters Component
 *
 * Filtering controls for tables
 */

import { MagnifyingGlassIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { Input, Select, cn } from '@medsuite/ui-components';
import { Column, Table } from '@tanstack/react-table';
import { type InputHTMLAttributes, forwardRef, useEffect, useMemo, useState } from 'react';

export interface FilterOption {
  label: string;
  value: string;
}

export interface ColumnFilter {
  columnId: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'date';
  options?: FilterOption[];
  placeholder?: string;
}

export interface TableFiltersProps {
  filters: ColumnFilter[];
  onFilterChange?: (columnId: string, value: unknown) => void;
  onClearFilters?: () => void;
  className?: string;
  showClearButton?: boolean;
  layout?: 'horizontal' | 'vertical';
}

export const TableFilters = forwardRef<HTMLDivElement, TableFiltersProps>(
  (
    {
      filters,
      onFilterChange,
      onClearFilters,
      className,
      showClearButton = true,
      layout = 'horizontal',
    },
    ref
  ) => {
    const [filterValues, setFilterValues] = useState<Record<string, string>>({});

    const handleFilterChange = (columnId: string, value: string) => {
      const newValues = { ...filterValues, [columnId]: value };
      setFilterValues(newValues);
      onFilterChange?.(columnId, value);
    };

    const handleClearFilters = () => {
      setFilterValues({});
      onClearFilters?.();
    };

    const hasActiveFilters = Object.values(filterValues).some(
      (value) => value !== undefined && value !== '' && value !== null
    );

    return (
      <div ref={ref} className={cn('rounded-lg border border-gray-200 bg-white p-4', className)}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
            <FunnelIcon className="h-4 w-4" />
            <span>Filters</span>
          </div>
          {showClearButton && hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
            >
              <XMarkIcon className="h-4 w-4" />
              <span>Clear all</span>
            </button>
          )}
        </div>

        <div
          className={cn(
            'gap-4',
            layout === 'horizontal'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              : 'flex flex-col'
          )}
        >
          {filters.map((filter) => (
            <div key={filter.columnId}>
              {filter.type === 'text' && (
                <Input
                  label={filter.label}
                  type="text"
                  value={filterValues[filter.columnId] || ''}
                  onChange={(e) => handleFilterChange(filter.columnId, e.target.value)}
                  placeholder={filter.placeholder || `Filter by ${filter.label.toLowerCase()}...`}
                  size="sm"
                  iconBefore={<MagnifyingGlassIcon className="h-4 w-4" />}
                />
              )}

              {filter.type === 'number' && (
                <Input
                  label={filter.label}
                  type="number"
                  value={filterValues[filter.columnId] || ''}
                  onChange={(e) => handleFilterChange(filter.columnId, e.target.value)}
                  placeholder={filter.placeholder || `Filter by ${filter.label.toLowerCase()}...`}
                  size="sm"
                />
              )}

              {filter.type === 'date' && (
                <Input
                  label={filter.label}
                  type="date"
                  value={filterValues[filter.columnId] || ''}
                  onChange={(e) => handleFilterChange(filter.columnId, e.target.value)}
                  size="sm"
                />
              )}

              {filter.type === 'select' && filter.options && (
                <Select
                  label={filter.label}
                  value={filterValues[filter.columnId] || ''}
                  onChange={(value) => handleFilterChange(filter.columnId, String(value))}
                  size="sm"
                  options={[
                    { value: '', label: 'All' },
                    ...filter.options.map((option) => ({
                      value: option.value,
                      label: option.label,
                    })),
                  ]}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
);

TableFilters.displayName = 'TableFilters';

/**
 * DebouncedInput - Input component with debounced onChange
 */
interface DebouncedInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
}

export function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce, onChange]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      className={cn(
        'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm',
        'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
        props.className
      )}
    />
  );
}

/**
 * ColumnFilterInput - Generic column filter component for TanStack Table
 */
interface ColumnFilterInputProps {
  column: Column<unknown, unknown>;
  table: Table<unknown>;
}

export function ColumnFilterInput({ column, table }: ColumnFilterInputProps) {
  const firstValue = table.getPreFilteredRowModel().flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      typeof firstValue === 'number'
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column, firstValue]
  );

  return typeof firstValue === 'number' ? (
    <div className="flex space-x-2">
      <DebouncedInput
        type="number"
        min={Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
        max={Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
        value={(columnFilterValue as [number, number])?.[0] ?? ''}
        onChange={(value) => column.setFilterValue((old: [number, number]) => [value, old?.[1]])}
        placeholder={`Min ${
          column.getFacetedMinMaxValues()?.[0] ? `(${column.getFacetedMinMaxValues()?.[0]})` : ''
        }`}
      />
      <DebouncedInput
        type="number"
        min={Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
        max={Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
        value={(columnFilterValue as [number, number])?.[1] ?? ''}
        onChange={(value) => column.setFilterValue((old: [number, number]) => [old?.[0], value])}
        placeholder={`Max ${
          column.getFacetedMinMaxValues()?.[1] ? `(${column.getFacetedMinMaxValues()?.[1]})` : ''
        }`}
      />
    </div>
  ) : (
    <>
      <datalist id={column.id + 'list'}>
        {sortedUniqueValues.slice(0, 5000).map((value: string) => (
          <option value={value} key={value} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '') as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
        list={column.id + 'list'}
      />
    </>
  );
}
